import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import { useAuth } from "../context/AuthContext";
import {
  listQuestionBank,
  createBankQuestion,
  updateBankQuestion,
  deleteBankQuestion,
  listBusinessInterviewTypes,
  listBusinessTopics,
  listPrepxaiQuestions,
  importPrepxaiQuestions,
} from "../api/client";

// Question Bank — a company authors its own interview questions in the SAME
// format the internal admin dashboard uses (they live in the shared
// interview_questions table, tagged with this org's id) and can import ("clone")
// any global prepxai question into its bank. Bank questions never enter an
// interview automatically; a recruiter explicitly seeds them onto a round (see
// JobCreate's "Ask from question bank"). Writes are gated to owner/admin/
// recruiter; viewers get a read-only list.

const WRITER_ROLES = ["owner", "admin", "recruiter"];
const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const LANGUAGES = ["python", "cpp", "javascript", "go"];

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const textareaClass =
  "flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const DIFFICULTY_BADGE = {
  easy: "bg-success/15 text-success",
  medium: "bg-warning/15 text-warning",
  hard: "bg-destructive/15 text-destructive",
};

// ── small helpers ────────────────────────────────────────────────────────────

// tryParseJSON returns { value, error }. An empty string is valid (value = null).
function tryParseJSON(text) {
  const s = String(text || "").trim();
  if (!s) return { value: null, error: "" };
  try {
    return { value: JSON.parse(s), error: "" };
  } catch (e) {
    return { value: undefined, error: e.message || "Invalid JSON" };
  }
}

function questionTitle(q) {
  const t = q?.problemStatement?.title;
  return (typeof t === "string" && t.trim()) || "Untitled question";
}

function questionDescription(q) {
  const d = q?.problemStatement?.description;
  return typeof d === "string" ? d : "";
}

function asArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function getExamples(q) {
  return asArray(q?.problemStatement?.examples);
}

function topicNameOf(topic) {
  if (!topic) return "";
  if (typeof topic === "string") return topic;
  return topic.name || topic.Name || "";
}

function topicTypeOf(topic) {
  if (!topic || typeof topic === "string") return "";
  return topic.interview_type || topic.interviewType || topic.type || topic.Type || "";
}

function normalizeTopicsPayload(value) {
  const rows = Array.isArray(value?.topics) ? value.topics : Array.isArray(value) ? value : [];
  return rows
    .map((topic) => ({ name: topicNameOf(topic), type: topicTypeOf(topic) }))
    .filter((topic) => topic.name);
}

function uniqueSorted(items) {
  return [...new Set(items.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function DifficultyBadge({ value }) {
  const key = String(value || "").toLowerCase();
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize " +
        (DIFFICULTY_BADGE[key] || "bg-muted text-muted-foreground")
      }
    >
      {value || "—"}
    </span>
  );
}

// Decompose a stored problemStatement into friendly title/description plus a JSON
// string of any remaining keys, so an edit round-trips extra fields losslessly.
function splitProblemStatement(ps) {
  const obj = ps && typeof ps === "object" ? { ...ps } : {};
  const title = typeof obj.title === "string" ? obj.title : "";
  const description = typeof obj.description === "string" ? obj.description : "";
  const examples = Array.isArray(obj.examples) ? obj.examples : [];
  delete obj.title;
  delete obj.description;
  delete obj.examples;
  const extra = Object.keys(obj).length ? JSON.stringify(obj, null, 2) : "";
  return { title, description, examples, extra };
}

// Build the form state for a create (blank) or an edit (from a loaded question).
function formFromQuestion(q) {
  const ps = splitProblemStatement(q?.problemStatement);
  return {
    type: q?.type || "",
    difficulty: q?.difficulty || "Medium",
    title: ps.title,
    description: ps.description,
    problemStatementExtra: ps.extra,
    topics: asArray(q?.topics),
    expectedTimeMinutes:
      q?.expectedTimeMinutes != null ? String(q.expectedTimeMinutes) : "",
    timeComplexity: q?.timeComplexity || "",
    spaceComplexity: q?.spaceComplexity || "",
    problemExplanation: q?.problemExplanation || "",
    examples: ps.examples.length ? ps.examples : [{ input: "", output: "", explanation: "" }],
    boilerplates:
      q?.boilerplates && typeof q.boilerplates === "object" && !Array.isArray(q.boilerplates)
        ? { python: "", cpp: "", javascript: "", go: "", ...q.boilerplates }
        : { python: "", cpp: "", javascript: "", go: "" },
    testCases: Array.isArray(q?.testCases) && q.testCases.length
      ? q.testCases
      : [{ input: "", output: "", is_hidden: false }],
  };
}

function Field({ label, hint, required, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium leading-none text-foreground">
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

function MultiSelect({ options, selected, onChange, placeholder = "Select options" }) {
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const label = selected?.length ? selected.join(", ") : placeholder;

  const toggle = (option) => {
    const next = selectedSet.has(option)
      ? (selected || []).filter((item) => item !== option)
      : [...(selected || []), option];
    onChange(next);
  };

  return (
    <div className="relative">
      <button
        type="button"
        className={selectClass + " justify-between text-left"}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={(selected?.length ? "text-foreground" : "text-muted-foreground") + " truncate"}>
          {label}
        </span>
        <Icon name="ChevronDown" size={15} className="ml-2 flex-shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <>
          <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No options available</div>
            ) : (
              options.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggle(option)}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <span className="truncate">{option}</span>
                  {selectedSet.has(option) && <Icon name="Check" size={14} className="text-primary" />}
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            aria-label="Close selector"
            className="fixed inset-0 z-20 cursor-default"
            onClick={() => setOpen(false)}
          />
        </>
      )}
    </div>
  );
}

// ── create / edit modal ──────────────────────────────────────────────────────

function QuestionFormModal({ open, onClose, initial, typeOptions, topicRows, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => formFromQuestion(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [bpTab, setBpTab] = useState("python");

  // Reset the form whenever the modal is (re)opened for a different question.
  useEffect(() => {
    if (open) {
      setForm(formFromQuestion(initial));
      setError("");
      setSaving(false);
    }
  }, [open, initial]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const updateExample = (index, field, value) => {
    setForm((prev) => {
      const examples = [...prev.examples];
      examples[index] = { ...examples[index], [field]: value };
      return { ...prev, examples };
    });
  };

  const updateTestCase = (index, field, value) => {
    setForm((prev) => {
      const testCases = [...prev.testCases];
      testCases[index] = { ...testCases[index], [field]: value };
      return { ...prev, testCases };
    });
  };

  const buildPayload = () => {
    const type = form.type.trim();
    const difficulty = form.difficulty.trim();
    const title = form.title.trim();
    const description = form.description.trim();

    if (!type) return { error: "Choose an interview type." };
    if (!difficulty) return { error: "Choose a difficulty." };
    if (!title) return { error: "Enter a question title." };

    const extra = tryParseJSON(form.problemStatementExtra);
    if (extra.error) return { error: "Saved advanced problem data is invalid: " + extra.error };
    const base = extra.value && typeof extra.value === "object" ? extra.value : {};

    const payload = {
      type,
      difficulty,
      problemStatement: { ...base, title, description, examples: form.examples || [] },
      companies: [],
      topics: form.topics || [],
      stages: [],
      problemExplanation: form.problemExplanation.trim(),
      timeComplexity: form.timeComplexity.trim(),
      spaceComplexity: form.spaceComplexity.trim(),
      boilerplates: form.boilerplates || {},
      testCases: form.testCases || [],
    };
    const mins = Number(form.expectedTimeMinutes);
    if (Number.isFinite(mins) && mins > 0) payload.expectedTimeMinutes = mins;
    return { payload };
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const built = buildPayload();
    if (built.error) {
      setError(built.error);
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await updateBankQuestion(initial.id, built.payload);
      } else {
        await createBankQuestion(built.payload);
      }
      onSaved?.();
    } catch (err) {
      setError(err?.error || "Could not save the question.");
      setSaving(false);
    }
  };

  // Preserve the current type as an option even if it isn't in the catalog (e.g.
  // an imported question authored with a legacy type).
  const typeChoices = useMemo(() => {
    const set = new Set(typeOptions);
    if (form.type) set.add(form.type);
    return Array.from(set);
  }, [typeOptions, form.type]);
  const topicChoices = useMemo(() => {
    const currentType = String(form.type || "").trim().toLowerCase();
    const typed = topicRows.filter((topic) => {
      const topicType = String(topic.type || "").trim().toLowerCase();
      return !topicType || !currentType || topicType === currentType;
    });
    return uniqueSorted((typed.length ? typed : topicRows).map((topic) => topic.name));
  }, [form.type, topicRows]);

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      size="xl"
      title={isEdit ? "Edit question" : "New question"}
      description="Authored in the same format your interviews use. Bank questions only run when a recruiter seeds them onto a round."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving} iconName="Check">
            {isEdit ? "Save changes" : "Create question"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Interview type" required>
            <select
              className={selectClass}
              value={form.type}
              onChange={(e) => set({ type: e.target.value, topics: [] })}
            >
              <option value="" disabled>
                Select a type…
              </option>
              {typeChoices.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Difficulty" required>
            <select
              className={selectClass}
              value={form.difficulty}
              onChange={(e) => set({ difficulty: e.target.value })}
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Input
          label="Title"
          required
          placeholder="e.g. Two Sum, Design a URL shortener"
          value={form.title}
          onChange={(e) => set({ title: e.target.value })}
        />

        <Field label="Description" hint="The problem shown to the candidate.">
          <textarea
            className={textareaClass + " min-h-[120px] font-sans"}
            placeholder="Describe the task, constraints, and expectations…"
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Expected time (min)">
            <input
              type="number"
              min="1"
              className={selectClass}
              placeholder="e.g. 30"
              value={form.expectedTimeMinutes}
              onChange={(e) => set({ expectedTimeMinutes: e.target.value })}
            />
          </Field>
          <Field label="Time complexity">
            <input
              className={selectClass}
              placeholder="e.g. O(n)"
              value={form.timeComplexity}
              onChange={(e) => set({ timeComplexity: e.target.value })}
            />
          </Field>
          <Field label="Space complexity">
            <input
              className={selectClass}
              placeholder="e.g. O(1)"
              value={form.spaceComplexity}
              onChange={(e) => set({ spaceComplexity: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Topics">
            <MultiSelect
              options={topicChoices}
              selected={form.topics}
              onChange={(items) => set({ topics: items })}
              placeholder="Select topics"
            />
          </Field>
          <Field label="LLM instructions">
            <input
              className={selectClass}
              placeholder="How should the interviewer guide this question?"
              value={form.problemExplanation}
              onChange={(e) => set({ problemExplanation: e.target.value })}
            />
          </Field>
        </div>

        {/* Advanced / optional structured fields */}
        <details className="rounded-lg border border-border bg-muted/20 px-4 py-3">
          <summary className="cursor-pointer select-none text-sm font-medium text-foreground">
            Advanced
          </summary>
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-lg border border-border bg-background">
                <div className="flex h-10 items-center justify-between border-b border-border px-3">
                  <div className="text-sm font-medium text-foreground">Examples</div>
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    iconName="Plus"
                    onClick={() => set({ examples: [...form.examples, { input: "", output: "", explanation: "" }] })}
                  >
                    Add
                  </Button>
                </div>
                <div className="max-h-64 space-y-2 overflow-y-auto p-3">
                  {form.examples.map((example, index) => (
                    <div key={index} className="rounded-lg border border-border bg-muted/30 p-2">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Example {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => set({ examples: form.examples.filter((_, i) => i !== index) })}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Remove example"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                      <input
                        value={example.input || ""}
                        onChange={(e) => updateExample(index, "input", e.target.value)}
                        className={selectClass + " mb-2 h-8 font-mono text-xs"}
                        placeholder="Input"
                      />
                      <input
                        value={example.output || ""}
                        onChange={(e) => updateExample(index, "output", e.target.value)}
                        className={selectClass + " mb-2 h-8 font-mono text-xs"}
                        placeholder="Output"
                      />
                      <textarea
                        value={example.explanation || ""}
                        onChange={(e) => updateExample(index, "explanation", e.target.value)}
                        className={textareaClass + " min-h-[56px] font-sans text-xs"}
                        placeholder="Explanation"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-background">
                <div className="flex h-10 items-center justify-between border-b border-border px-3">
                  <div className="text-sm font-medium text-foreground">Test cases</div>
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    iconName="Plus"
                    onClick={() => set({ testCases: [...form.testCases, { input: "", output: "", is_hidden: false }] })}
                  >
                    Add
                  </Button>
                </div>
                <div className="max-h-64 space-y-2 overflow-y-auto p-3">
                  {form.testCases.map((testCase, index) => (
                    <div key={index} className="rounded-lg border border-border bg-muted/30 p-2">
                      <div className="mb-2 flex items-center justify-between">
                        <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={!!testCase.is_hidden}
                            onChange={(e) => updateTestCase(index, "is_hidden", e.target.checked)}
                            className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                          />
                          Hidden case
                        </label>
                        <button
                          type="button"
                          onClick={() => set({ testCases: form.testCases.filter((_, i) => i !== index) })}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Remove test case"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                      <input
                        value={testCase.input || ""}
                        onChange={(e) => updateTestCase(index, "input", e.target.value)}
                        className={selectClass + " mb-2 h-8 font-mono text-xs"}
                        placeholder="Raw input"
                      />
                      <input
                        value={testCase.output || ""}
                        onChange={(e) => updateTestCase(index, "output", e.target.value)}
                        className={selectClass + " h-8 font-mono text-xs"}
                        placeholder="Expected output"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-border bg-background">
              <div className="flex border-b border-border bg-muted/30">
                {LANGUAGES.map((language) => (
                  <button
                    type="button"
                    key={language}
                    onClick={() => setBpTab(language)}
                    className={
                      "flex-1 px-3 py-2 text-xs font-semibold uppercase transition-colors " +
                      (bpTab === language
                        ? "bg-background text-primary"
                        : "text-muted-foreground hover:text-foreground")
                    }
                  >
                    {language}
                  </button>
                ))}
              </div>
              <textarea
                value={form.boilerplates[bpTab] || ""}
                onChange={(e) =>
                  set({
                    boilerplates: { ...form.boilerplates, [bpTab]: e.target.value },
                  })
                }
                className="h-32 w-full resize-none bg-slate-950 p-3 font-mono text-xs text-emerald-300 outline-none"
                placeholder={`Starter code for ${bpTab}`}
              />
            </div>
          </div>
        </details>
      </form>
    </Modal>
  );
}

// ── import-from-prepxai modal ────────────────────────────────────────────────

function ImportModal({ open, onClose, typeOptions, onImported }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [typeFilter, setTypeFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      if (search.trim()) params.q = search.trim();
      const data = await listPrepxaiQuestions(params);
      setItems(data.questions || []);
    } catch (err) {
      setLoadError(err?.error || "Could not load the catalog.");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, difficultyFilter, search]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setResult(null);
  }, [open]);

  // Debounced (re)load while the modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [open, load]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const doImport = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setImporting(true);
    setResult(null);
    try {
      const res = await importPrepxaiQuestions(ids);
      setResult(res);
      setSelected(new Set());
      await load(); // refresh "imported" flags
      onImported?.();
    } catch (err) {
      setLoadError(err?.error || "Could not import the questions.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={importing ? undefined : onClose}
      size="xl"
      title="Import from prepxai"
      description="Clone questions from the global prepxai catalog into your bank. Already-imported questions are skipped."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={importing}>
            Close
          </Button>
          <Button
            onClick={doImport}
            loading={importing}
            disabled={selected.size === 0}
            iconName="Download"
          >
            Import {selected.size > 0 ? `(${selected.size})` : ""}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {result && (
          <div className="flex items-start gap-2 rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">
            <Icon name="CheckCircle2" size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              Imported {result.imported} question{result.imported === 1 ? "" : "s"}
              {result.skipped ? `, skipped ${result.skipped} (already in your bank)` : ""}.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <select
            className={selectClass}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            {typeOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <select
            className={selectClass}
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
          >
            <option value="">All difficulties</option>
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <input
            className={selectClass}
            placeholder="Search title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loadError && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <Icon name="AlertCircle" size={16} />
            <span>{loadError}</span>
          </div>
        )}

        <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
          {loading ? (
            [0, 1, 2, 3].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-muted/50" />
            ))
          ) : items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
              No prepxai questions match these filters.
            </div>
          ) : (
            items.map((q) => {
              const already = !!q.imported;
              const checked = selected.has(q.id);
              return (
                <label
                  key={q.id}
                  className={
                    "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors " +
                    (already
                      ? "cursor-not-allowed border-border bg-muted/30 opacity-70"
                      : checked
                      ? "border-primary/50 bg-primary/5"
                      : "border-border hover:border-primary/30 hover:bg-muted/30")
                  }
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring disabled:opacity-50"
                    checked={checked}
                    disabled={already}
                    onChange={() => toggle(q.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">
                        {questionTitle(q)}
                      </span>
                      {already && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Imported
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="rounded bg-muted px-1.5 py-0.5">{q.type}</span>
                      <DifficultyBadge value={q.difficulty} />
                      {Array.isArray(q.topics) && q.topics.length > 0 && (
                        <span className="truncate">{q.topics.slice(0, 3).join(", ")}</span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── delete confirm ───────────────────────────────────────────────────────────

function DeleteConfirm({ target, onCancel, onConfirm, busy }) {
  return (
    <Modal
      open={!!target}
      onClose={busy ? undefined : onCancel}
      size="sm"
      title="Delete question?"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} loading={busy} iconName="Trash2">
            Delete
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{target ? questionTitle(target) : ""}</span>{" "}
        will be removed from your bank. Rounds that already reference it keep running;
        this only stops it being seeded into new interviews. This cannot be undone.
      </p>
    </Modal>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function QuestionBank() {
  const { organization } = useAuth();
  const canWrite = WRITER_ROLES.includes(organization?.role);

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [typeOptions, setTypeOptions] = useState([]);
  const [topicRows, setTopicRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [search, setSearch] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // question object, or null for create
  const [importOpen, setImportOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      if (search.trim()) params.q = search.trim();
      const data = await listQuestionBank(params);
      setQuestions(data.questions || []);
    } catch (err) {
      setLoadError(err?.error || "Could not load the question bank.");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, difficultyFilter, search]);

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(load, 250);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [load]);

  // Interview-type + topic catalogs power the form selectors.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [typesData, topicsData] = await Promise.all([
          listBusinessInterviewTypes(),
          listBusinessTopics(),
        ]);
        if (!alive) return;
        setTypeOptions((typesData.interviewTypes || []).map((t) => t.name));
        setTopicRows(normalizeTopicsPayload(topicsData));
      } catch {
        /* non-fatal — existing questions still render */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (q) => {
    setEditing(q);
    setFormOpen(true);
  };

  const onSaved = () => {
    setFormOpen(false);
    setEditing(null);
    load();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteBankQuestion(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setLoadError(err?.error || "Could not delete the question.");
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = !!(typeFilter || difficultyFilter || search.trim());

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Question Bank</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Author your own interview questions or import from prepxai, then seed
            them onto a job's rounds.
          </p>
        </div>
        {canWrite && (
          <div className="flex flex-shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" iconName="Download" onClick={() => setImportOpen(true)}>
              Import from prepxai
            </Button>
            <Button size="sm" iconName="Plus" onClick={openCreate}>
              New question
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <select
          className={selectClass}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          {typeOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          value={difficultyFilter}
          onChange={(e) => setDifficultyFilter(e.target.value)}
        >
          <option value="">All difficulties</option>
          {DIFFICULTIES.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <div className="relative">
          <Icon
            name="Search"
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            className={selectClass + " pl-9"}
            placeholder="Search title or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Error */}
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-muted/50" />
          ))}
        </div>
      ) : questions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name="Library" size={22} />
          </div>
          <h2 className="mt-4 text-base font-semibold text-foreground">
            {hasFilters ? "No questions match these filters" : "No questions yet"}
          </h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            {hasFilters
              ? "Try a different type, difficulty, or search."
              : "Author your first question, or import a set from the prepxai catalog."}
          </p>
          {canWrite && !hasFilters && (
            <div className="mt-5 flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" iconName="Download" onClick={() => setImportOpen(true)}>
                Import from prepxai
              </Button>
              <Button size="sm" iconName="Plus" onClick={openCreate}>
                New question
              </Button>
            </div>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {questions.map((q) => {
            const desc = questionDescription(q);
            return (
              <li
                key={q.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-base font-semibold text-foreground">
                      {questionTitle(q)}
                    </h3>
                    {q.imported && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                        <Icon name="Download" size={10} /> Imported
                      </span>
                    )}
                  </div>
                  {desc && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{desc}</p>
                  )}
                  <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">
                      {q.type}
                    </span>
                    <DifficultyBadge value={q.difficulty} />
                    {q.expectedTimeMinutes != null && (
                      <span className="inline-flex items-center gap-1">
                        <Icon name="Clock" size={12} />
                        {q.expectedTimeMinutes} min
                      </span>
                    )}
                    {Array.isArray(q.topics) && q.topics.length > 0 && (
                      <span className="truncate">{q.topics.slice(0, 4).join(", ")}</span>
                    )}
                  </div>
                </div>
                {canWrite && (
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      onClick={() => openEdit(q)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label="Edit question"
                      title="Edit"
                    >
                      <Icon name="Pencil" size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(q)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      aria-label="Delete question"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Modals */}
      {formOpen && (
        <QuestionFormModal
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          initial={editing}
          typeOptions={typeOptions}
          topicRows={topicRows}
          onSaved={onSaved}
        />
      )}
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        typeOptions={typeOptions}
        onImported={load}
      />
      <DeleteConfirm
        target={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        busy={deleting}
      />
    </div>
  );
}
